import { useEffect, useRef, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { peek } from '../api.js';
import { when } from '../format.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { Dialog, MoreOptions, toast } from '../ui.js';

/**
 * The client's brand profile (owner 2026-11-03, the idea from BrandSync): read and sorted by the Hub from the client's
 * brand folders in Drive (Brand/LOGO, FONT, GRAPHIC, MOGRT, MUSIC and Customer_Brand_profile). On the client card for
 * employees, at the top of a project's Brief tab for the whole team. Nothing in Drive is moved; a person corrects here.
 */

export const PARTS = ['logos', 'fonts', 'graphics', 'pdf', 'music', 'sfx', 'references', 'projectFiles', 'other'] as const;
export type Part = (typeof PARTS)[number];

export interface BrandFile {
  id: string;
  name: string;
  mimeType: string;
  size: number | null;
  path: string;
  part: Part;
  sub: string | null;
  manual: boolean;
  /** "current": use this one; "old": do not use */
  status: 'current' | 'old' | null;
  /** used in projects: how many, the latest one's name (null when not to be shown), and whether in this project */
  used: { count: number; last: string | null; lastAt: string; here: boolean } | null;
  driveUrl: string | null;
  src: string | null;
  download: string | null;
}
export interface BrandView {
  clientId: string;
  clientName: string;
  colors: { hex: string; name: string | null; auto: boolean }[];
  colorsManual: boolean;
  fonts: { name: string; use: string | null; auto: boolean }[];
  fontsManual: boolean;
  logoRule: string | null;
  tone: string | null;
  avoid: string | null;
  website: string | null;
  folders: { id: string; name: string; url: string }[];
  syncedAt: string | null;
  syncError: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  files: BrandFile[];
  counts: Record<Part, number>;
  logos: BrandFile[];
  canEdit: boolean;
  canSync: boolean;
  stale: boolean;
  /** what Klara read in the brand guide PDF: how many colours, fonts and texts it found, or why it could not */
  guide: { fileId: string; name: string; readAt: string; error: string | null; colors: number; fonts: number; texts: number } | null;
  canReadGuide: boolean;
  /** a guide PDF Klara has not read yet (or a newer version): the card asks for the reading */
  guideDue: boolean;
}

const TX = texts(
  {
    title: 'Brand profile',
    projectTitle: "Client's brand",
    edit: 'Edit',
    showAll: 'Show all',
    part: { logos: 'Logos', fonts: 'Fonts', graphics: 'Graphics', pdf: 'Brand guide (PDF)', music: 'Music', sfx: 'Sound effects', references: 'Reference films', projectFiles: 'Project files', other: 'Other' } as Record<Part, string>,
    headings: 'Headings',
    font: 'Font',
    logoRule: 'Logo in film',
    tone: 'Tone',
    avoid: 'Avoid',
    website: 'Website',
    colors: 'Colours',
    copied: (hex: string) => `${hex} copied`,
    copy: (hex: string) => `Copy ${hex}`,
    fromDrive: (at: string) => `Read from Drive ${at}`,
    editedBy: (who: string, at: string) => ` · edited by ${who} ${at}`,
    reading: 'Reading the brand folders in Drive…',
    readNow: 'Read Drive again',
    guideReading: 'Klara is reading the brand guide…',
    guideFrom: (name: string, at: string) => `Colours, fonts and rules from ${name}, read by Klara ${at}`,
    guideEmpty: (name: string) => `Klara found no colours, fonts or rules in ${name}.`,
    guideFailed: (name: string, why: string) => `Klara could not read ${name}: ${why}`,
    guideWhy: { too_big: 'the file is over 20 MB', cut_short: 'the answer was cut short', no_answer: 'no answer in the right form' } as Record<string, string>,
    readGuide: 'Read the brand guide again',
    noFolder: 'No brand folder found in Drive. Put the files in "{Client} - Internal/Brand" (LOGO, FONT, GRAPHIC, MOGRT, MUSIC) or "Customer_Brand_profile", and the Hub sorts them by itself.',
    failed: (e: string) => `Could not read Drive: ${e}`,
    empty: 'Nothing here yet.',
    noDrive: 'Drive is not connected, so the Hub cannot read the brand folders.',
    folders: 'Brand folders in Drive',
    download: 'Download',
    openDrive: 'Open in Drive',
    moveTo: 'Belongs under',
    sub: 'Own group (optional)',
    moved: 'Moved. The Hub keeps it there.',
    sortedNote: 'The Hub sorts the files by folder, type and name. Nothing is moved in Drive.',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    saved: 'Brand profile saved',
    hex: 'Hex',
    colorName: 'Name (optional)',
    addColor: 'Add colour',
    addFont: 'Add font',
    use: 'Used for (optional)',
    remove: 'Remove',
    autoBack: 'Fill colours and fonts from the files again',
    autoNote: 'What you save here is kept: the nightly read of Drive no longer changes it.',
    projectNote: 'In this project',
    projectNoteLabel: 'How this project departs from the brand (optional)',
    projectNoteHint: 'For example: Christmas red #C8102E instead of orange.',
    noteSaved: 'Saved',
    sample: 'Aa Bb Cc 123 – Klara färdiga film',
    nFiles: (n: number) => `${n} ${n === 1 ? 'file' : 'files'}`,
    play: 'Play',
    guide: 'Brand guide',
    show: 'Show',
    prev: 'Previous',
    next: 'Next',
    nOf: (i: number, n: number) => `${i} of ${n}`,
    openTab: 'Open in a new tab',
    noPdf: 'The PDF could not be shown here.',
    opening: 'Opening…',
    page: (i: number, n: number) => `Page ${i} of ${n}`,
    noShow: 'The file could not be shown.',
    pickFromFiles: 'Pick colours from the files',
    hintImg: 'Click in the image to pick a colour.',
    hintPdf: 'Click in the brand guide to pick a colour.',
    addPicked: (n: number) => `Add ${n} to the colours`,
    addedColors: 'Colours added',
    tooMany: 'At most 12 colours. Remove some first.',
    transparent: 'That spot is transparent.',
    chooseColor: 'Choose colour',
    addFiles: 'Add files',
    addFilesHint: 'Logos, icons, fonts, music … They go into the Brand folder in Drive and are sorted at once.',
    uploading: (i: number, n: number, name: string, pct: number) => `Uploading ${i} of ${n}: ${name} (${pct}%)`,
    added: (n: number) => `${n} ${n === 1 ? 'file' : 'files'} added to Drive`,
    mark: 'Mark',
    status: { none: 'No mark', current: '★ Use this one', old: 'Old, do not use' },
    oldOnes: (n: number) => `Old versions (${n})`,
    marked: 'Marked',
    usedIn: 'Used in this project',
    lastUsed: (name: string, n: number) => (n > 1 ? `Last used: ${name} · ${n} projects` : `Last used: ${name}`),
    usedCount: (n: number) => `Used in ${n} ${n === 1 ? 'project' : 'projects'}`,
    usedHere: 'Used in this project:',
    usedOn: 'Marked as used in this project',
    usedOff: 'No longer marked as used'
  },
  {
    title: 'Brandprofil',
    projectTitle: 'Kundens brand',
    edit: 'Ändra',
    showAll: 'Visa allt',
    part: { logos: 'Loggor', fonts: 'Typsnitt', graphics: 'Grafik', pdf: 'Grafisk manual (PDF)', music: 'Musik', sfx: 'Ljudeffekter', references: 'Referensfilmer', projectFiles: 'Projektfiler', other: 'Övrigt' },
    headings: 'Rubriker',
    font: 'Typsnitt',
    logoRule: 'Logo i film',
    tone: 'Ton',
    avoid: 'Undvik',
    website: 'Webbplats',
    colors: 'Färger',
    copied: (hex: string) => `${hex} kopierad`,
    copy: (hex: string) => `Kopiera ${hex}`,
    fromDrive: (at: string) => `Läst från Drive ${at}`,
    editedBy: (who: string, at: string) => ` · ändrad av ${who} ${at}`,
    reading: 'Läser brand-mapparna i Drive…',
    readNow: 'Läs Drive igen',
    guideReading: 'Klara läser den grafiska manualen…',
    guideFrom: (name: string, at: string) => `Färger, typsnitt och regler ur ${name}, läst av Klara ${at}`,
    guideEmpty: (name: string) => `Klara hittade inga färger, typsnitt eller regler i ${name}.`,
    guideFailed: (name: string, why: string) => `Klara kunde inte läsa ${name}: ${why}`,
    guideWhy: { too_big: 'filen är större än 20 MB', cut_short: 'svaret blev avklippt', no_answer: 'inget svar i rätt form' } as Record<string, string>,
    readGuide: 'Läs manualen igen',
    noFolder: 'Ingen brand-mapp hittades i Drive. Lägg filerna i "{Kund} - Internal/Brand" (LOGO, FONT, GRAPHIC, MOGRT, MUSIC) eller "Customer_Brand_profile", så sorterar Hubben dem själv.',
    failed: (e: string) => `Kunde inte läsa Drive: ${e}`,
    empty: 'Inget här än.',
    noDrive: 'Drive är inte anslutet, så Hubben kan inte läsa brand-mapparna.',
    folders: 'Brand-mappar i Drive',
    download: 'Ladda ner',
    openDrive: 'Öppna i Drive',
    moveTo: 'Hör till',
    sub: 'Egen grupp (valfritt)',
    moved: 'Flyttad. Hubben låter den ligga kvar där.',
    sortedNote: 'Hubben sorterar filerna efter mapp, typ och namn. Inget flyttas i Drive.',
    save: 'Spara',
    cancel: 'Avbryt',
    close: 'Stäng',
    saved: 'Brandprofilen sparad',
    hex: 'Hex',
    colorName: 'Namn (valfritt)',
    addColor: 'Lägg till färg',
    addFont: 'Lägg till typsnitt',
    use: 'Används till (valfritt)',
    remove: 'Ta bort',
    autoBack: 'Fyll i färger och typsnitt från filerna igen',
    autoNote: 'Det du sparar här ligger kvar: Drive-läsningen på natten ändrar det inte.',
    projectNote: 'I det här projektet',
    projectNoteLabel: 'Hur projektet avviker från brandet (valfritt)',
    projectNoteHint: 'Till exempel: julröd #C8102E i stället för orange.',
    noteSaved: 'Sparat',
    sample: 'Aa Bb Cc 123 – Klara färdiga film',
    nFiles: (n: number) => `${n} ${n === 1 ? 'fil' : 'filer'}`,
    play: 'Spela',
    guide: 'Grafisk manual',
    show: 'Visa',
    prev: 'Föregående',
    next: 'Nästa',
    nOf: (i: number, n: number) => `${i} av ${n}`,
    openTab: 'Öppna i ny flik',
    noPdf: 'PDF:en kunde inte visas här.',
    opening: 'Öppnar…',
    page: (i: number, n: number) => `Sida ${i} av ${n}`,
    noShow: 'Filen kunde inte visas.',
    pickFromFiles: 'Plocka färger ur filerna',
    hintImg: 'Klicka i bilden för att plocka en färg.',
    hintPdf: 'Klicka i manualen för att plocka en färg.',
    addPicked: (n: number) => `Lägg till ${n} i färgerna`,
    addedColors: 'Färgerna tillagda',
    tooMany: 'Högst 12 färger. Ta bort några först.',
    transparent: 'Den punkten är genomskinlig.',
    chooseColor: 'Välj färg',
    addFiles: 'Lägg till filer',
    addFilesHint: 'Loggor, ikoner, typsnitt, musik … De hamnar i Brand-mappen i Drive och sorteras direkt.',
    uploading: (i: number, n: number, name: string, pct: number) => `Laddar upp ${i} av ${n}: ${name} (${pct} %)`,
    added: (n: number) => (n === 1 ? '1 fil tillagd i Drive' : `${n} filer tillagda i Drive`),
    mark: 'Märkning',
    status: { none: 'Ingen märkning', current: '★ Använd den här', old: 'Gammal, använd inte' },
    oldOnes: (n: number) => `Gamla versioner (${n})`,
    marked: 'Märkt',
    usedIn: 'Använd i projektet',
    lastUsed: (name: string, n: number) => (n > 1 ? `Senast använd: ${name} · ${n} projekt` : `Senast använd: ${name}`),
    usedCount: (n: number) => `Använd i ${n} projekt`,
    usedHere: 'Använda i projektet:',
    usedOn: 'Markerad som använd i projektet',
    usedOff: 'Inte längre markerad som använd'
  }
);
type T = (typeof TX)['en'];

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const isImage = (f: BrandFile) => Boolean(f.src) && (f.mimeType.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg)$/i.test(f.name));
const isFont = (f: BrandFile) => Boolean(f.src) && /\.(ttf|otf|woff2?)$/i.test(f.name);
const isAudio = (f: BrandFile) => Boolean(f.src) && f.mimeType.startsWith('audio/');
const isPdf = (f: BrandFile) => Boolean(f.src) && (f.mimeType === 'application/pdf' || /\.pdf$/i.test(f.name));
/** Files the viewer shows (and colours can be picked from): the brand guide and images. */
const viewable = (f: BrandFile) => isPdf(f) || isImage(f);
/** The brand guides first, then logos and graphics: where colours are picked from. */
/** A download from a project's Brief notes the file as used there, by itself (a failure is not shown). */
const noteDownload = (api: Api, projectId: string, f: BrandFile) =>
  api.post<{ ok: true }>(`/api/projects/${projectId}/brand/files/${encodeURIComponent(f.id)}/use`, { how: 'download' }).then(
    () => true,
    () => false
  );

/** "Last used: Vinterkampanj · 3 projects"; nothing when the only use is the project it is shown on (the tick says it). */
function usedLine(f: BrandFile, t: T): string | null {
  const u = f.used;
  if (!u || (u.here && u.count === 1)) return null;
  return u.last ? t.lastUsed(u.last, u.count) : t.usedCount(u.count);
}

const pickSources = (b: BrandView) => [...b.files.filter((f) => f.part === 'pdf' && isPdf(f)), ...b.files.filter((f) => f.part !== 'pdf' && viewable(f))];

export const toHex = (r: number, g: number, b: number) =>
  `#${[r, g, b]
    .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

/** The average colour of the opaque pixels in an RGBA block (the pipette reads 3×3), or null when it is mostly transparent. */
export function averageHex(data: ArrayLike<number>): string | null {
  let r = 0;
  let g = 0;
  let b = 0;
  let n = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3]! < 128) continue;
    r += data[i]!;
    g += data[i + 1]!;
    b += data[i + 2]!;
    n++;
  }
  return n && n * 2 >= data.length / 4 ? toHex(r / n, g / n, b / n) : null;
}

/** "#aabbcc" or "rgb(1, 2, 3)" (what browsers' pipettes give) as "#AABBCC". */
export function cssHex(v: string): string | null {
  const h = /^#?([0-9a-f]{6})$/i.exec(v.trim());
  if (h) return `#${h[1]!.toUpperCase()}`;
  const m = /^rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i.exec(v.trim());
  return m ? toHex(Number(m[1]), Number(m[2]), Number(m[3])) : null;
}

/** The profile's colours with the picked ones added at the end (no doubles). */
export function mergeColors(have: { hex: string; name: string | null }[], add: string[]) {
  const seen = new Set(have.map((c) => c.hex.toUpperCase()));
  const out = have.map((c) => ({ hex: c.hex, name: c.name }));
  for (const h of add) {
    if (seen.has(h.toUpperCase())) continue;
    seen.add(h.toUpperCase());
    out.push({ hex: h, name: null });
  }
  return out;
}

/** Saves picked colours to the profile; null when they did not fit or the save failed (a toast says why). */
async function addColors(api: Api, b: BrandView, hexes: string[], t: T): Promise<BrandView | null> {
  const colors = mergeColors(b.colors, hexes);
  if (colors.length > 12) {
    toast({ text: t.tooMany });
    return null;
  }
  try {
    const r = await api.put<{ brand: BrandView }>(`/api/clients/${b.clientId}/brand`, { colors });
    toast({ text: t.addedColors });
    return r.brand;
  } catch (e) {
    toast({ text: errText(e) });
    return null;
  }
}

/** The colour at a point of a drawn canvas (3×3 average), or null where it is transparent. */
function colourOn(ctx: CanvasRenderingContext2D, w: number, h: number, px: number, py: number): string | null {
  const x = Math.min(w - 1, Math.max(0, Math.round(px) - 1));
  const y = Math.min(h - 1, Math.max(0, Math.round(py) - 1));
  try {
    return averageHex(ctx.getImageData(x, y, Math.min(3, w - x), Math.min(3, h - y)).data);
  } catch {
    return null;
  }
}

/** pdf.js, loaded the first time a brand guide is opened (it is big, and most pages never need it). */
async function openPdf(src: string) {
  const [lib, worker] = await Promise.all([import('pdfjs-dist'), import('pdfjs-dist/build/pdf.worker.min.mjs?url')]);
  lib.GlobalWorkerOptions.workerSrc = worker.default;
  const r = await fetch(src);
  if (!r.ok) throw new Error(String(r.status));
  // the loading task: .promise gives the document, .destroy() frees it (and the worker)
  return lib.getDocument({ data: new Uint8Array(await r.arrayBuffer()) });
}

/** Loads a font file once and gives the family name to style a sample with (null until loaded or when it fails). */
const loaded = new Map<string, Promise<string | null>>();
function useFontFace(f: BrandFile | undefined): string | null {
  const [family, setFamily] = useState<string | null>(null);
  useEffect(() => {
    if (!f?.src || typeof FontFace === 'undefined') return;
    let p = loaded.get(f.id);
    if (!p) {
      const name = `kffbrand-${f.id}`;
      p = new FontFace(name, `url(${f.src})`)
        .load()
        .then((face) => {
          document.fonts.add(face);
          return name;
        })
        .catch(() => null);
      loaded.set(f.id, p);
    }
    let live = true;
    void p.then((n) => live && setFamily(n));
    return () => {
      live = false;
    };
  }, [f?.id]);
  return family;
}

function Swatches(p: { colors: BrandView['colors']; t: T; small?: boolean }) {
  const copy = async (hex: string) => {
    try {
      await navigator.clipboard.writeText(hex);
      toast({ text: p.t.copied(hex) });
    } catch {
      /* no clipboard: the hex is on screen */
    }
  };
  return (
    <div class={p.small ? 'bp-sw bp-sw--small' : 'bp-sw'}>
      {p.colors.map((c) => (
        <button type="button" class="bp-c" title={p.t.copy(c.hex)} aria-label={p.t.copy(c.hex)} onClick={() => void copy(c.hex)}>
          <b style={{ background: c.hex }} />
          {p.small ? null : <span>{c.name ?? c.hex}</span>}
        </button>
      ))}
    </div>
  );
}

function FontLine(p: { font: BrandView['fonts'][number]; file?: BrandFile; t: T }) {
  const family = useFontFace(p.file);
  return (
    <div class="bp-row">
      <span>{p.font.use ?? p.t.font}</span>
      <span style={family ? { fontFamily: `'${family}', sans-serif` } : undefined}>{p.font.name}</span>
    </div>
  );
}

/** Font files by the name the profile shows, so the card can write each font in itself. */
const fontFileOf = (b: BrandView, name: string) => b.files.find((f) => f.part === 'fonts' && isFont(f) && f.name.toLowerCase().replace(/[^a-z0-9]/g, '').startsWith(name.toLowerCase().replace(/[^a-z0-9]/g, '')));

function Summary(p: { b: BrandView; t: T }) {
  const { b, t } = p;
  return (
    <>
      {b.logos.length ? (
        <div class="bp-logos">
          {b.logos.slice(0, 2).map((f) => (
            <img src={f.src!} alt={f.name} loading="lazy" />
          ))}
        </div>
      ) : null}
      {b.colors.length ? <Swatches colors={b.colors} t={t} /> : null}
      {b.fonts.map((f) => (
        <FontLine font={f} file={fontFileOf(b, f.name)} t={t} />
      ))}
      {b.logoRule ? (
        <div class="bp-row">
          <span>{t.logoRule}</span>
          <span>{b.logoRule}</span>
        </div>
      ) : null}
      {b.website ? (
        <div class="bp-row">
          <span>{t.website}</span>
          <a href={`https://${b.website}`} target="_blank" rel="noopener">
            {b.website}
          </a>
        </div>
      ) : null}
      {b.tone ? (
        <p class="bp-text">
          <b>{t.tone}:</b> {b.tone}
        </p>
      ) : null}
      {b.avoid ? (
        <p class="bp-text">
          <b>{t.avoid}:</b> {b.avoid}
        </p>
      ) : null}
      <p class="bp-parts small">
        {PARTS.filter((k) => k !== 'logos' && b.counts[k]).map((k) => (
          <span>
            {t.part[k]} {b.counts[k]}
          </span>
        ))}
      </p>
    </>
  );
}

const hasContent = (b: BrandView) => b.files.length > 0 || b.colors.length > 0 || b.fonts.length > 0 || Boolean(b.tone || b.logoRule || b.avoid);

/** An image to look at; with `pick` a click reads the colour under the pointer (3×3 average). */
function PickImage(p: { f: BrandFile; pick: boolean; onPick: (hex: string) => void; t: T }) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const [hover, setHover] = useState<string | null>(null);
  const img = useRef<HTMLImageElement>(null);
  const cv = useRef<{ ctx: CanvasRenderingContext2D; w: number; h: number } | null>(null);
  useEffect(() => {
    // the bytes come through fetch, so the canvas may read them (an <img> from the API would taint it)
    let live = true;
    let made: string | null = null;
    setUrl(null);
    setFailed(false);
    cv.current = null;
    fetch(p.f.src!)
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.blob();
      })
      .then((blob) => {
        if (!live) return;
        made = URL.createObjectURL(blob);
        setUrl(made);
      })
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [p.f.id]);
  const colourAt = (e: MouseEvent): string | null => {
    const el = img.current;
    if (!el) return null;
    const r = el.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = Math.max(1, Math.round(r.width * dpr));
    const h = Math.max(1, Math.round(r.height * dpr));
    if (!cv.current || cv.current.w !== w || cv.current.h !== h) {
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d', { willReadFrequently: true });
      if (!ctx) return null;
      ctx.drawImage(el, 0, 0, w, h);
      cv.current = { ctx, w, h };
    }
    return colourOn(cv.current.ctx, w, h, (e.clientX - r.left) * dpr, (e.clientY - r.top) * dpr);
  };
  if (failed) return <p class="muted">{p.t.noShow}</p>;
  return (
    <div class="bp-view-img">
      {url ? (
        <img
          ref={img}
          src={url}
          alt={p.f.name}
          class={p.pick ? 'is-picking' : undefined}
          onClick={(e) => {
            if (!p.pick) return;
            const h = colourAt(e);
            if (h) p.onPick(h);
            else toast({ text: p.t.transparent });
          }}
          onMouseMove={(e) => p.pick && setHover(colourAt(e))}
          onMouseLeave={() => setHover(null)}
        />
      ) : (
        <span class="muted small">…</span>
      )}
      {p.pick && hover ? (
        <span class="bp-hover" aria-hidden="true">
          <b style={{ background: hover }} />
          {hover}
        </span>
      ) : null}
    </div>
  );
}

/** A PDF drawn page by page in the Hub (pdf.js); with `pick` a click reads the colour under the pointer. */
function PdfPages(p: { f: BrandFile; pick: boolean; onPick: (hex: string) => void; t: T }) {
  type Doc = Awaited<Awaited<ReturnType<typeof openPdf>>['promise']>;
  const [doc, setDoc] = useState<Doc | null>(null);
  const [failed, setFailed] = useState(false);
  const [page, setPage] = useState(1);
  const [hover, setHover] = useState<string | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    let live = true;
    let task: Awaited<ReturnType<typeof openPdf>> | null = null;
    setDoc(null);
    setFailed(false);
    setPage(1);
    openPdf(p.f.src!)
      .then((t) => {
        task = t;
        if (!live) void t.destroy();
        return t.promise;
      })
      .then(
        (d) => live && setDoc(d),
        () => live && setFailed(true)
      );
    return () => {
      live = false;
      if (task) void task.destroy();
    };
  }, [p.f.id]);
  useEffect(() => {
    const el = canvas.current;
    if (!doc || !el) return;
    let task: { cancel(): void; promise: Promise<unknown> } | null = null;
    let live = true;
    void doc.getPage(page).then((pg) => {
      if (!live) return;
      // the whole page in view: as wide as the dialog, but never taller than 60% of the screen
      const width = Math.max(200, (box.current?.clientWidth || 800) - 16);
      const height = Math.min(window.innerHeight * 0.6, 900);
      const base = pg.getViewport({ scale: 1 });
      const dpr = window.devicePixelRatio || 1;
      const viewport = pg.getViewport({ scale: Math.min(width / base.width, height / base.height) * dpr });
      el.width = Math.round(viewport.width);
      el.height = Math.round(viewport.height);
      el.style.width = `${Math.round(viewport.width / dpr)}px`;
      task = pg.render({ canvas: el, viewport });
      task.promise.catch(() => {
        /* cancelled by the next page */
      });
    }, () => live && setFailed(true));
    return () => {
      live = false;
      task?.cancel();
    };
  }, [doc, page]);
  const colourAt = (e: MouseEvent) => {
    const el = canvas.current;
    const ctx = el?.getContext('2d', { willReadFrequently: true });
    if (!el || !ctx) return null;
    const r = el.getBoundingClientRect();
    return colourOn(ctx, el.width, el.height, ((e.clientX - r.left) * el.width) / r.width, ((e.clientY - r.top) * el.height) / r.height);
  };
  if (failed) return <p class="muted">{p.t.noPdf}</p>;
  const n = doc?.numPages ?? 0;
  return (
    <div class="bp-view-pdf" ref={box}>
      {n > 1 ? (
        <div class="bp-pages small">
          <button type="button" class="btn btn-sm btn-ghost" aria-label={p.t.prev} disabled={page === 1} onClick={() => setPage(page - 1)}>
            ‹
          </button>
          <span class="muted">{p.t.page(page, n)}</span>
          <button type="button" class="btn btn-sm btn-ghost" aria-label={p.t.next} disabled={page === n} onClick={() => setPage(page + 1)}>
            ›
          </button>
        </div>
      ) : null}
      {doc ? null : <span class="muted small">{p.t.opening}</span>}
      <canvas
        ref={canvas}
        class={p.pick ? 'is-picking' : undefined}
        aria-label={p.f.name}
        onClick={(e) => {
          if (!p.pick) return;
          const h = colourAt(e);
          if (h) p.onPick(h);
        }}
        onMouseMove={(e) => p.pick && setHover(colourAt(e))}
        onMouseLeave={() => setHover(null)}
      />
      {p.pick && hover ? (
        <span class="bp-hover" aria-hidden="true">
          <b style={{ background: hover }} />
          {hover}
        </span>
      ) : null}
    </div>
  );
}

/**
 * The brand guide (PDF) and images, inside the Hub. With `onColors` (people who may edit the profile) colours are
 * picked from them with a click, as in BrandSync; the picked ones are added in one go.
 */
export function BrandViewer(p: { files: BrandFile[]; start: number; staff: boolean; onClose: () => void; onColors?: (hexes: string[]) => Promise<boolean> | boolean; onDownload?: (f: BrandFile) => void }) {
  const t = TX[useLang()];
  const [i, setI] = useState(Math.max(0, Math.min(p.start, p.files.length - 1)));
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const f = p.files[i];
  if (!f) return null;
  const pick = (h: string) => setPicked((x) => (x.includes(h) ? x : [...x, h]));
  const add = async () => {
    setBusy(true);
    try {
      if (await p.onColors!(picked)) setPicked([]);
    } finally {
      setBusy(false);
    }
  };
  const n = p.files.length;
  return (
    <Dialog
      wide
      title={f.name}
      onClose={p.onClose}
      foot={
        <>
          {n > 1 ? (
            <span class="bp-nav">
              <button type="button" class="btn btn-sm btn-ghost" aria-label={t.prev} disabled={i === 0} onClick={() => setI(i - 1)}>
                ‹
              </button>
              <span class="small muted">{t.nOf(i + 1, n)}</span>
              <button type="button" class="btn btn-sm btn-ghost" aria-label={t.next} disabled={i === n - 1} onClick={() => setI(i + 1)}>
                ›
              </button>
            </span>
          ) : null}
          {isPdf(f) ? (
            <a class="btn btn-ghost" href={f.src!} target="_blank" rel="noopener">
              {t.openTab}
            </a>
          ) : null}
          {f.download ? (
            <a class="btn btn-ghost" href={f.download} rel="noopener" onClick={() => p.onDownload?.(f)}>
              {t.download}
            </a>
          ) : null}
          {p.staff && f.driveUrl ? (
            <a class="btn btn-ghost" href={f.driveUrl} target="_blank" rel="noopener">
              {t.openDrive}
            </a>
          ) : null}
          <button type="button" class="btn" onClick={p.onClose}>
            {t.close}
          </button>
        </>
      }
    >
      {isPdf(f) ? <PdfPages key={f.id} f={f} pick={Boolean(p.onColors)} onPick={pick} t={t} /> : <PickImage key={f.id} f={f} pick={Boolean(p.onColors)} onPick={pick} t={t} />}
      {p.onColors ? (
        <div class="bp-pickbar">
          <span class="small muted">{isPdf(f) ? t.hintPdf : t.hintImg}</span>
          {picked.length ? (
            <div class="bp-picked">
              {picked.map((h) => (
                <button type="button" class="bp-chip" title={`${t.remove} ${h}`} aria-label={`${t.remove} ${h}`} onClick={() => setPicked(picked.filter((x) => x !== h))}>
                  <b style={{ background: h }} />
                  {h} ×
                </button>
              ))}
              <button type="button" class="btn btn-sm btn-primary" disabled={busy} onClick={() => void add()}>
                {t.addPicked(picked.length)}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Dialog>
  );
}

/** "Brand guide" in a block's title row: opens the guide (PDF) in the viewer; editors pick colours from it there. */
function GuideButton(p: { api: Api; b: BrandView; staff: boolean; onChanged?: (b: BrandView) => void; onDownload?: (f: BrandFile) => void }) {
  const t = TX[useLang()];
  const [open, setOpen] = useState(false);
  const guides = p.b.files.filter((f) => f.part === 'pdf' && isPdf(f));
  if (!guides.length) return null;
  const onColors =
    p.b.canEdit && p.staff
      ? async (hexes: string[]) => {
          const b = await addColors(p.api, p.b, hexes, t);
          if (b) p.onChanged?.(b);
          return Boolean(b);
        }
      : undefined;
  return (
    <>
      <button type="button" class="btn btn-sm" onClick={() => setOpen(true)}>
        {t.guide}
      </button>
      {open ? <BrandViewer files={guides} start={0} staff={p.staff} onClose={() => setOpen(false)} onColors={onColors} onDownload={p.onDownload} /> : null}
    </>
  );
}

/** Sends one file to the Hub in chunks; the Hub passes each to the client's Brand folder in Drive. */
async function uploadBrandFile(api: Api, clientId: string, file: File, progress: (sent: number) => void) {
  const s = await api.post<{ token: string; chunk: number }>(`/api/clients/${clientId}/brand/upload`, { name: file.name, size: file.size, mimeType: file.type || undefined });
  let offset = 0;
  for (;;) {
    const q = new URLSearchParams({ t: s.token, offset: String(offset) });
    const step = await api.putBytes<{ done: false; received: number } | { done: true; file: { id: string; name: string } }>(`/api/clients/${clientId}/brand/upload?${q}`, file.slice(offset, Math.min(offset + s.chunk, file.size)));
    if (step.done) return step.file;
    // Google says how much it holds; carry on from there
    offset = step.received;
    progress(offset);
  }
}

/** "Add files": uploads into the Brand folder in Drive, then reads it again so the files are sorted in at once. */
function AddFiles(p: { api: Api; b: BrandView; t: T; onDone: (b: BrandView) => void }) {
  const { t } = p;
  const [now, setNow] = useState<string | null>(null);
  const start = async (list: FileList | null) => {
    const files = [...(list ?? [])].filter((f) => f.size > 0);
    if (!files.length || now) return;
    let ok = 0;
    const failed: string[] = [];
    for (const [i, f] of files.entries()) {
      const show = (sent: number) => setNow(t.uploading(i + 1, files.length, f.name, Math.floor((sent / f.size) * 100)));
      show(0);
      try {
        await uploadBrandFile(p.api, p.b.clientId, f, show);
        ok++;
      } catch (e) {
        failed.push(`${f.name}: ${errText(e)}`);
      }
    }
    try {
      if (ok) p.onDone((await p.api.post<{ brand: BrandView }>(`/api/clients/${p.b.clientId}/brand/sync`)).brand);
    } catch (e) {
      failed.push(errText(e));
    }
    setNow(null);
    toast({ text: [ok ? t.added(ok) : '', ...failed].filter(Boolean).join(' · ') });
  };
  return (
    <div class="bp-add-files">
      <label class={`btn btn-sm${now ? ' is-disabled' : ''}`}>
        {t.addFiles}
        <input
          type="file"
          multiple
          class="sr-only"
          disabled={Boolean(now)}
          onChange={(e) => {
            const input = e.currentTarget as HTMLInputElement;
            void start(input.files);
            input.value = '';
          }}
        />
      </label>
      <span class="small muted" aria-live="polite">
        {now ?? t.addFilesHint}
      </span>
    </div>
  );
}

/** A part's files in the order the dialog lists them: "use this one" first, then own group and name; old ones apart. */
const partFiles = (b: BrandView, k: Part, old = false) =>
  b.files
    .filter((f) => f.part === k && (f.status === 'old') === old)
    .sort((x, y) => Number(y.status === 'current') - Number(x.status === 'current') || (x.sub ?? '').localeCompare(y.sub ?? '') || x.name.localeCompare(y.name));

/**
 * The whole profile, part by part, with previews; a producer moves a file to another part here. Opened on a project
 * (`projectId`), each file can be ticked "Used in this project", and a download from here notes it by itself.
 */
export function BrandDialog(p: { api: Api; b: BrandView; onClose: () => void; onChanged?: (b: BrandView) => void; staff: boolean; projectId?: string }) {
  const t = TX[useLang()];
  const [b, setB] = useState(p.b);
  const [view, setView] = useState<{ files: BrandFile[]; start: number } | null>(null);
  const pv = p.projectId ? `/api/projects/${p.projectId}/brand` : null;
  const changed = (n: BrandView) => {
    setB(n);
    p.onChanged?.(n);
  };
  /** an answer from the client card's API: on a project, the project's view is read again (old files left out, "used here" filled in) */
  const fromClient = async (n: BrandView) => {
    if (!pv) return changed(n);
    try {
      changed((await p.api.get<ProjectBrandView | undefined>(pv))?.brand ?? n);
    } catch {
      changed(n);
    }
  };
  const onColors =
    b.canEdit && p.staff
      ? async (hexes: string[]) => {
          const n = await addColors(p.api, b, hexes, t);
          if (n) await fromClient(n);
          return Boolean(n);
        }
      : undefined;
  /** moves a file ({part, sub}) or marks it ({status}); the Hub keeps both */
  const move = async (f: BrandFile, body: { part: string; sub: string | null } | { status: BrandFile['status'] }) => {
    try {
      const r = await p.api.put<{ brand: BrandView }>(`/api/clients/${b.clientId}/brand/files/${encodeURIComponent(f.id)}`, body);
      await fromClient(r.brand);
      toast({ text: 'status' in body ? t.marked : t.moved });
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  /** the tick "Used in this project" */
  const use = async (f: BrandFile, on: boolean) => {
    const url = `${pv}/files/${encodeURIComponent(f.id)}/use`;
    try {
      const r = on ? await p.api.post<ProjectBrandView>(url, { how: 'manual' }) : await p.api.del<ProjectBrandView>(url);
      changed(r.brand);
      toast({ text: on ? t.usedOn : t.usedOff });
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  /** a download from the project: noted by itself, and the tick shows it */
  const downloaded = (f: BrandFile) => {
    if (!p.projectId) return;
    void noteDownload(p.api, p.projectId, f).then((ok) => {
      if (ok && !f.used?.here) void fromClient(b);
    });
  };
  const item = (f: BrandFile, k: Part, old: boolean) => (
    <FileItem
      f={f}
      t={t}
      canMove={b.canEdit && p.staff}
      staff={p.staff}
      onMove={move}
      onUse={pv ? use : undefined}
      onDownload={pv ? downloaded : undefined}
      onView={
        viewable(f)
          ? () => {
              const files = partFiles(b, k, old).filter(viewable);
              setView({ files, start: files.indexOf(f) });
            }
          : undefined
      }
    />
  );
  return (
    <Dialog
      title={`${t.title} · ${b.clientName}`}
      onClose={p.onClose}
      sub={t.sortedNote}
      foot={
        <button type="button" class="btn" onClick={p.onClose}>
          {t.close}
        </button>
      }
    >
      <div class="bp-all">
        {b.canEdit && p.staff ? <AddFiles api={p.api} b={b} t={t} onDone={(n) => void fromClient(n)} /> : null}
        <Summary b={b} t={t} />
        {PARTS.filter((k) => b.counts[k]).map((k) => (
          <section class="bp-part">
            <h3>
              {t.part[k]} <span class="muted">{b.counts[k]}</span>
            </h3>
            <ul class={k === 'logos' || k === 'graphics' ? 'bp-files bp-files--grid' : 'bp-files'}>{partFiles(b, k).map((f) => item(f, k, false))}</ul>
            {partFiles(b, k, true).length ? (
              <details class="bp-old">
                <summary class="small muted">{t.oldOnes(partFiles(b, k, true).length)}</summary>
                <ul class={k === 'logos' || k === 'graphics' ? 'bp-files bp-files--grid' : 'bp-files'}>{partFiles(b, k, true).map((f) => item(f, k, true))}</ul>
              </details>
            ) : null}
          </section>
        ))}
        {!b.files.length ? <p class="muted">{b.syncError === 'no_folder' ? t.noFolder : t.empty}</p> : null}
      </div>
      {view ? <BrandViewer files={view.files} start={view.start} staff={p.staff} onClose={() => setView(null)} onColors={onColors} onDownload={pv ? downloaded : undefined} /> : null}
    </Dialog>
  );
}

function FileItem(p: {
  f: BrandFile;
  t: T;
  canMove: boolean;
  staff: boolean;
  onMove: (f: BrandFile, body: { part: string; sub: string | null } | { status: BrandFile['status'] }) => void;
  onView?: () => void;
  /** on a project: the tick "Used in this project" */
  onUse?: (f: BrandFile, on: boolean) => void;
  onDownload?: (f: BrandFile) => void;
}) {
  const { f, t } = p;
  const used = usedLine(f, t);
  const family = useFontFace(isFont(f) ? f : undefined);
  const [sub, setSub] = useState(f.sub ?? '');
  return (
    <li class={f.status === 'old' ? 'bp-file is-old' : f.status === 'current' ? 'bp-file is-current' : 'bp-file'}>
      {f.status ? <span class={f.status === 'current' ? 'pill pill--ok' : 'pill pill--neutral'}>{t.status[f.status]}</span> : null}
      {isImage(f) ? (
        p.onView ? (
          <button type="button" class="bp-thumb-btn" aria-label={`${t.show} ${f.name}`} onClick={p.onView}>
            <img class="bp-thumb" src={f.src!} alt="" loading="lazy" />
          </button>
        ) : (
          <img class="bp-thumb" src={f.src!} alt="" loading="lazy" />
        )
      ) : null}
      {isFont(f) ? <span class="bp-font-sample" style={family ? { fontFamily: `'${family}', sans-serif` } : undefined}>{t.sample}</span> : null}
      {isAudio(f) ? <audio controls preload="none" src={f.src!} aria-label={`${t.play} ${f.name}`} /> : null}
      <span class="bp-file-name" title={f.path}>
        {f.sub ? <span class="pill pill--neutral">{f.sub}</span> : null} {f.name}
      </span>
      {used ? <span class="bp-used small muted">{used}</span> : null}
      <span class="bp-file-acts small">
        {p.onView ? (
          <button type="button" class="linklike" onClick={p.onView}>
            {t.show}
          </button>
        ) : null}
        {f.download ? (
          <a href={f.download} rel="noopener" onClick={() => p.onDownload?.(f)}>
            {t.download}
          </a>
        ) : null}
        {p.staff && f.driveUrl ? (
          <a href={f.driveUrl} target="_blank" rel="noopener">
            {t.openDrive}
          </a>
        ) : null}
      </span>
      {p.onUse ? (
        <label class="bp-use small">
          <input type="checkbox" checked={Boolean(f.used?.here)} onChange={(e) => p.onUse!(f, (e.currentTarget as HTMLInputElement).checked)} /> {t.usedIn}
        </label>
      ) : null}
      {p.canMove ? (
        <span class="bp-move small">
          <label>
            {t.moveTo}{' '}
            <select class="input input--quiet" value={f.part} onChange={(e) => p.onMove(f, { part: (e.currentTarget as HTMLSelectElement).value, sub: sub.trim() || null })}>
              {PARTS.map((k) => (
                <option value={k}>{t.part[k]}</option>
              ))}
            </select>
          </label>
          <input class="input input--quiet" placeholder={t.sub} value={sub} maxLength={60} onInput={(e) => setSub((e.currentTarget as HTMLInputElement).value)} onBlur={() => sub.trim() !== (f.sub ?? '') && p.onMove(f, { part: f.part, sub: sub.trim() || null })} />
          <label>
            {t.mark}{' '}
            <select class="input input--quiet" value={f.status ?? ''} onChange={(e) => p.onMove(f, { status: ((e.currentTarget as HTMLSelectElement).value || null) as BrandFile['status'] })}>
              <option value="">{t.status.none}</option>
              <option value="current">{t.status.current}</option>
              <option value="old">{t.status.old}</option>
            </select>
          </label>
        </span>
      ) : null}
    </li>
  );
}

/** Correct the colours, fonts and words; the files themselves stay in Drive. */
function BrandEditDialog(p: { api: Api; b: BrandView; onClose: () => void; onSaved: (b: BrandView) => void }) {
  const t = TX[useLang()];
  const [colors, setColors] = useState(p.b.colors.map((c) => ({ hex: c.hex, name: c.name ?? '' })));
  const [fonts, setFonts] = useState(p.b.fonts.map((f) => ({ name: f.name, use: f.use ?? '' })));
  const [logoRule, setLogoRule] = useState(p.b.logoRule ?? '');
  const [tone, setTone] = useState(p.b.tone ?? '');
  const [avoid, setAvoid] = useState(p.b.avoid ?? '');
  const [website, setWebsite] = useState(p.b.website ?? '');
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState(false);
  const sources = pickSources(p.b);
  const val = (e: Event) => (e.currentTarget as HTMLInputElement).value;
  const send = async (body: Record<string, unknown>) => {
    setBusy(true);
    try {
      const r = await p.api.put<{ brand: BrandView }>(`/api/clients/${p.b.clientId}/brand`, body);
      p.onSaved(r.brand);
      toast({ text: t.saved });
      p.onClose();
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setBusy(false);
    }
  };
  const colorsChanged = JSON.stringify(colors) !== JSON.stringify(p.b.colors.map((c) => ({ hex: c.hex, name: c.name ?? '' })));
  const fontsChanged = JSON.stringify(fonts) !== JSON.stringify(p.b.fonts.map((f) => ({ name: f.name, use: f.use ?? '' })));
  const save = () =>
    send({
      ...(colorsChanged ? { colors: colors.filter((c) => c.hex.trim()).map((c) => ({ hex: c.hex.trim(), name: c.name.trim() || null })) } : {}),
      ...(fontsChanged ? { fonts: fonts.filter((f) => f.name.trim()).map((f) => ({ name: f.name.trim(), use: f.use.trim() || null })) } : {}),
      logoRule,
      tone,
      avoid,
      website
    });
  return (
    <Dialog
      title={`${t.title} · ${p.b.clientName}`}
      onClose={p.onClose}
      onSubmit={() => void save()}
      sub={t.autoNote}
      foot={
        <>
          <button type="button" class="btn btn-ghost" onClick={p.onClose}>
            {t.cancel}
          </button>
          <button type="submit" class="btn btn-primary" disabled={busy}>
            {t.save}
          </button>
        </>
      }
    >
      <div class="stack">
        <fieldset class="fieldset">
          <legend class="label">{t.colors}</legend>
          {colors.map((c, i) => (
            <div class="bp-edit-row">
              <input type="color" class="bp-dot" aria-label={t.chooseColor} value={cssHex(c.hex)?.toLowerCase() ?? '#ffffff'} onInput={(e) => setColors(colors.map((x, j) => (j === i ? { ...x, hex: cssHex(val(e)) ?? x.hex } : x)))} />
              <input class="input" aria-label={t.hex} value={c.hex} maxLength={7} onInput={(e) => setColors(colors.map((x, j) => (j === i ? { ...x, hex: val(e) } : x)))} />
              <input class="input" aria-label={t.colorName} placeholder={t.colorName} value={c.name} maxLength={40} onInput={(e) => setColors(colors.map((x, j) => (j === i ? { ...x, name: val(e) } : x)))} />
              <button type="button" class="btn btn-ghost btn-sm" onClick={() => setColors(colors.filter((_, j) => j !== i))}>
                {t.remove}
              </button>
            </div>
          ))}
          <div class="cluster">
            {colors.length < 12 ? (
              <button type="button" class="btn btn-sm" onClick={() => setColors([...colors, { hex: '', name: '' }])}>
                {t.addColor}
              </button>
            ) : null}
            {sources.length && colors.length < 12 ? (
              <button type="button" class="btn btn-sm" onClick={() => setPicking(true)}>
                {t.pickFromFiles}
              </button>
            ) : null}
          </div>
        </fieldset>
        <fieldset class="fieldset">
          <legend class="label">{t.font}</legend>
          {fonts.map((f, i) => (
            <div class="bp-edit-row">
              <input class="input" aria-label={t.font} value={f.name} maxLength={80} onInput={(e) => setFonts(fonts.map((x, j) => (j === i ? { ...x, name: val(e) } : x)))} />
              <input class="input" aria-label={t.use} placeholder={t.use} value={f.use} maxLength={40} onInput={(e) => setFonts(fonts.map((x, j) => (j === i ? { ...x, use: val(e) } : x)))} />
              <button type="button" class="btn btn-ghost btn-sm" onClick={() => setFonts(fonts.filter((_, j) => j !== i))}>
                {t.remove}
              </button>
            </div>
          ))}
          {fonts.length < 8 ? (
            <button type="button" class="btn btn-sm bp-add" onClick={() => setFonts([...fonts, { name: '', use: '' }])}>
              {t.addFont}
            </button>
          ) : null}
        </fieldset>
        <div class="field">
          <label class="label" for="br-rule">
            {t.logoRule}
          </label>
          <input id="br-rule" class="input input--wide" value={logoRule} maxLength={300} onInput={(e) => setLogoRule(val(e))} />
        </div>
        <div class="field">
          <label class="label" for="br-tone">
            {t.tone}
          </label>
          <textarea id="br-tone" class="input input--wide" rows={2} value={tone} maxLength={1000} onInput={(e) => setTone(val(e))} />
        </div>
        <MoreOptions id="brand-edit" filled={[avoid, website].filter((x) => x.trim()).length}>
          <div class="stack">
            <div class="field">
              <label class="label" for="br-avoid">
                {t.avoid}
              </label>
              <textarea id="br-avoid" class="input input--wide" rows={2} value={avoid} maxLength={1000} onInput={(e) => setAvoid(val(e))} />
            </div>
            <div class="field">
              <label class="label" for="br-web">
                {t.website}
              </label>
              <input id="br-web" class="input input--wide" value={website} maxLength={200} placeholder="kund.se" onInput={(e) => setWebsite(val(e))} />
            </div>
            {p.b.colorsManual || p.b.fontsManual ? (
              <button type="button" class="btn btn-sm" disabled={busy} onClick={() => void send({ colors: null, fonts: null })}>
                {t.autoBack}
              </button>
            ) : null}
          </div>
        </MoreOptions>
      </div>
      {picking ? (
        <BrandViewer
          files={sources}
          start={0}
          staff
          onClose={() => setPicking(false)}
          onColors={(hexes) => {
            const next = mergeColors(
              colors.map((c) => ({ hex: c.hex, name: c.name })),
              hexes
            ).map((c) => ({ hex: c.hex, name: c.name ?? '' }));
            if (next.length > 12) {
              toast({ text: t.tooMany });
              return false;
            }
            setColors(next);
            setPicking(false);
            return true;
          }}
        />
      ) : null}
    </Dialog>
  );
}

/** Asks Drive again in the background when the last read is old; shows the answer when it comes. */
function useAutoSync(api: Api, b: BrandView | null, set: (b: BrandView) => void) {
  const [syncing, setSyncing] = useState(false);
  const asked = useRef('');
  const sync = async () => {
    if (!b) return;
    setSyncing(true);
    try {
      set((await api.post<{ brand: BrandView }>(`/api/clients/${b.clientId}/brand/sync`)).brand);
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setSyncing(false);
    }
  };
  useEffect(() => {
    if (b && b.stale && b.canSync && asked.current !== b.clientId) {
      asked.current = b.clientId;
      void sync();
    }
  }, [b?.clientId, b?.stale]);
  // a new or changed brand guide: Klara reads it once the folders are read (never both at once, the sync would undo it)
  const [readingGuide, setReadingGuide] = useState(false);
  const guideAsked = useRef('');
  const readGuide = async () => {
    if (!b) return;
    setReadingGuide(true);
    try {
      set((await api.post<{ brand: BrandView }>(`/api/clients/${b.clientId}/brand/guide`)).brand);
    } catch (e) {
      toast({ text: errText(e) });
    } finally {
      setReadingGuide(false);
    }
  };
  useEffect(() => {
    if (b && b.guideDue && b.canReadGuide && !syncing && !(b.stale && b.canSync) && guideAsked.current !== b.clientId) {
      guideAsked.current = b.clientId;
      void readGuide();
    }
  }, [b?.clientId, b?.guideDue, b?.stale, syncing]);
  return { syncing, sync, readingGuide, readGuide };
}

/** Where the colours, fonts and rules came from: the brand guide Klara read (or why it could not). */
function GuideLine(p: { b: BrandView; t: T; lang: Lang }) {
  const g = p.b.guide;
  if (!g) return null;
  if (g.error) return <p class="small muted">{p.t.guideFailed(g.name, p.t.guideWhy[g.error] ?? g.error)}</p>;
  return <p class="small muted">{g.colors + g.fonts + g.texts ? p.t.guideFrom(g.name, when(g.readAt, p.lang)) : p.t.guideEmpty(g.name)}</p>;
}

/** The client card's block (employees). */
export function ClientBrandBlock(p: { api: Api; clientId: string }) {
  const lang = useLang();
  const t = TX[lang];
  const url = `/api/clients/${p.clientId}/brand`;
  const [b, setB] = useState<BrandView | null>(() => peek<{ brand: BrandView }>(p.api, url)?.brand ?? null);
  const [all, setAll] = useState(false);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    p.api.get<{ brand: BrandView } | undefined>(url).then((r) => setB(r?.brand ?? null), () => setB(null));
  }, [p.clientId]);
  const { syncing, sync, readingGuide, readGuide } = useAutoSync(p.api, b, setB);
  if (!b) return null;
  return (
    <section class="blk bp-card" aria-labelledby="cc-brand" data-tour="client-brand">
      <div class="blk-head">
        <h2 class="blk-title" id="cc-brand">
          {t.title}
        </h2>
        <div class="blk-actions cluster">
          <GuideButton api={p.api} b={b} staff onChanged={setB} />
          {b.files.length || b.canEdit ? (
            <button type="button" class="btn btn-sm" onClick={() => setAll(true)}>
              {t.showAll}
            </button>
          ) : null}
          {b.canEdit ? (
            <button type="button" class="btn btn-sm" onClick={() => setEditing(true)}>
              {t.edit}
            </button>
          ) : null}
        </div>
      </div>
      {syncing && !hasContent(b) ? <p class="muted small">{t.reading}</p> : null}
      {hasContent(b) ? <Summary b={b} t={t} /> : !syncing ? <p class="muted small">{!b.canSync ? t.noDrive : b.syncError === 'no_folder' || !b.syncedAt ? t.noFolder : t.empty}</p> : null}
      {b.syncError && b.syncError !== 'no_folder' ? <p class="small field-error">{t.failed(b.syncError)}</p> : null}
      {readingGuide ? <p class="muted small">{t.guideReading}</p> : <GuideLine b={b} t={t} lang={lang} />}
      <p class="bp-src small muted">
        {b.syncedAt ? t.fromDrive(when(b.syncedAt, lang)) : null}
        {b.updatedBy && b.updatedAt ? t.editedBy(b.updatedBy, when(b.updatedAt, lang)) : null}
      </p>
      {b.canSync ? (
        <MoreOptions id="client-brand">
          <div class="stack">
            {b.folders.length ? (
              <p class="small">
                {t.folders}:{' '}
                {b.folders.map((f, i) => (
                  <>
                    {i ? ', ' : ''}
                    <a href={f.url} target="_blank" rel="noopener">
                      {f.name}
                    </a>
                  </>
                ))}
              </p>
            ) : null}
            <div class="cluster">
              <button type="button" class="btn btn-sm" disabled={syncing || readingGuide} onClick={() => void sync()}>
                {syncing ? t.reading : t.readNow}
              </button>
              {b.canReadGuide ? (
                <button type="button" class="btn btn-sm" disabled={syncing || readingGuide} onClick={() => void readGuide()}>
                  {readingGuide ? t.guideReading : t.readGuide}
                </button>
              ) : null}
            </div>
          </div>
        </MoreOptions>
      ) : null}
      {all ? <BrandDialog api={p.api} b={b} staff onClose={() => setAll(false)} onChanged={setB} /> : null}
      {editing ? <BrandEditDialog api={p.api} b={b} onClose={() => setEditing(false)} onSaved={setB} /> : null}
    </section>
  );
}

interface ProjectBrandView {
  brand: BrandView;
  note: string | null;
  canEditNote: boolean;
}

/** The top of a project's Brief tab: the client's brand in one row, for the whole team (freelancers too). */
export function ProjectBrandStrip(p: { api: Api; projectId: string; staff: boolean }) {
  const t = TX[useLang()];
  const url = `/api/projects/${p.projectId}/brand`;
  const [v, setV] = useState<ProjectBrandView | null>(() => peek<ProjectBrandView>(p.api, url) ?? null);
  const [all, setAll] = useState(false);
  const [note, setNote] = useState(v?.note ?? '');
  useEffect(() => {
    p.api.get<ProjectBrandView | undefined>(url).then(
      (r) => {
        setV(r?.brand ? r : null);
        setNote(r?.note ?? '');
      },
      () => setV(null)
    );
  }, [p.projectId]);
  if (!v?.brand) return null;
  const b = v.brand;
  if (!hasContent(b) && !v.note && !v.canEditNote) return null;
  const saveNote = async () => {
    try {
      const r = await p.api.put<ProjectBrandView>(url, { note });
      setV(r);
      toast({ text: t.noteSaved });
    } catch (e) {
      toast({ text: errText(e) });
    }
  };
  const font = b.fonts.map((f) => f.name).join(' / ');
  const usedHere = b.files.filter((f) => f.used?.here).map((f) => f.name);
  /** a download of the brand guide from the strip: noted by itself, then the strip is read again */
  const downloaded = (f: BrandFile) =>
    void noteDownload(p.api, p.projectId, f).then((ok) => {
      if (ok && !f.used?.here) p.api.get<ProjectBrandView | undefined>(url).then((r) => r?.brand && setV(r), () => undefined);
    });
  return (
    <section class="blk bp-strip" aria-labelledby="pj-brand" data-tour="project-brand">
      <div class="blk-head">
        <h2 class="blk-title" id="pj-brand">
          {t.projectTitle}
        </h2>
        {b.files.length ? (
          <div class="blk-actions cluster">
            <GuideButton api={p.api} b={b} staff={p.staff} onChanged={(n) => setV({ ...v, brand: n })} onDownload={downloaded} />
            <button type="button" class="btn btn-sm" onClick={() => setAll(true)}>
              {t.showAll}
            </button>
          </div>
        ) : null}
      </div>
      {hasContent(b) ? (
        <div class="bp-line">
          {b.logos[0] ? <img class="bp-line-logo" src={b.logos[0].src!} alt={b.logos[0].name} loading="lazy" /> : null}
          {b.colors.length ? <Swatches colors={b.colors} t={t} small /> : null}
          <span class="bp-line-text small">
            {[font, b.logoRule].filter(Boolean).join(' · ')}
            {b.tone ? (
              <>
                <br />
                {t.tone}: {b.tone}
              </>
            ) : null}
          </span>
        </div>
      ) : (
        <p class="muted small">{t.empty}</p>
      )}
      {v.note ? (
        <p class="bp-text">
          <b>{t.projectNote}:</b> {v.note}
        </p>
      ) : null}
      {usedHere.length ? (
        <p class="bp-text small">
          <b>{t.usedHere}</b> {usedHere.slice(0, 3).join(', ')}
          {usedHere.length > 3 ? ` +${usedHere.length - 3}` : ''}
        </p>
      ) : null}
      {v.canEditNote ? (
        <MoreOptions id="project-brand" filled={v.note ? 1 : 0}>
          <div class="field">
            <label class="label" for="pj-brand-note">
              {t.projectNoteLabel}
            </label>
            <input id="pj-brand-note" class="input input--wide" value={note} maxLength={500} placeholder={t.projectNoteHint} onInput={(e) => setNote((e.currentTarget as HTMLInputElement).value)} />
            <div>
              <button type="button" class="btn btn-sm" disabled={(note.trim() || null) === (v.note ?? null)} onClick={() => void saveNote()}>
                {t.save}
              </button>
            </div>
          </div>
        </MoreOptions>
      ) : null}
      {all ? <BrandDialog api={p.api} b={b} staff={p.staff} projectId={p.projectId} onClose={() => setAll(false)} onChanged={(n) => setV({ ...v, brand: n })} /> : null}
    </section>
  );
}
